<script setup lang="ts">
import { computed, ref } from 'vue';
import { useHead, useRoute } from '#app';
import AppNav from '@/components/AppNav.vue';
import FeaturesRoles from '@/components/features/FeaturesRoles.vue';
import AppFooter from '@/components/AppFooter.vue';
import { i18n, t } from '@/i18n';
import {
  absoluteLocalizedUrl,
  buildPathHreflangLinks,
  localizedPath,
  ogLocaleFor,
  siteLocaleFromRouteParam,
} from '@/lib/site-locale';

const route = useRoute();
const slug = computed(() => route.params.slug as string);
const siteLocale = computed(() => siteLocaleFromRouteParam(route.params.locale));
const solutionRoutePath = computed(() => `/solutions/${slug.value}`);
const canonicalUrl = computed(() =>
  absoluteLocalizedUrl(siteLocale.value, solutionRoutePath.value)
);
const solutionPath = (path: string) => localizedPath(siteLocale.value, path);

type SolutionFeature = {
  title: string;
  description: string;
};

type SolutionFaqItem = {
  question: string;
  answer: string;
};

type SolutionConfig = {
  title: string;
  subtitle: string;
  description: string;
  heroTitle: string;
  heroSubtitle: string;
  features: SolutionFeature[];
  ctaText: string;
  ctaLink: string;
  meta: {
    title: string;
    description: string;
  };
  faqItems: SolutionFaqItem[];
};

const slugConfigs = computed(() => {
  const raw = i18n.getRaw('landingPages.solutions.slug');
  return raw && typeof raw === 'object' ? (raw as Record<string, SolutionConfig>) : {};
});

const emptyConfig = (): SolutionConfig => ({
  title: '',
  subtitle: '',
  description: '',
  heroTitle: '',
  heroSubtitle: '',
  features: [],
  ctaText: 'Get Started',
  ctaLink: '/signup',
  meta: { title: '', description: '' },
  faqItems: [],
});

const currentConfig = computed((): SolutionConfig => {
  const config = slugConfigs.value[slug.value];
  if (config) {
    return {
      ...emptyConfig(),
      ...config,
      ctaLink: config.ctaLink || '/signup',
      features: config.features || [],
      faqItems: config.faqItems || [],
    };
  }
  const fallback = slugConfigs.value.default;
  if (fallback) {
    return {
      ...emptyConfig(),
      ...fallback,
      ctaLink: fallback.ctaLink || '/signup',
      features: fallback.features || [],
      faqItems: fallback.faqItems || [],
    };
  }
  return emptyConfig();
});

const teamSlugs = ['for-marketers', 'for-founders', 'for-creators', 'for-startups'] as const;

const teamCards = computed(() =>
  teamSlugs.map((teamSlug) => {
    const cfg = slugConfigs.value[teamSlug];
    return {
      slug: teamSlug,
      title: cfg?.title || '',
      description: cfg?.subtitle || '',
    };
  })
);

const faqSectionTitle = computed(() => t('landingPages.aiToolsIndex.seo_content.faq.title'));

const expandedFaqIndex = ref<number | null>(null);

const toggleFaq = (index: number) => {
  expandedFaqIndex.value = expandedFaqIndex.value === index ? null : index;
};

const faqItems = computed(() => currentConfig.value.faqItems || []);

const requestURL = useRequestURL();
const ogImageUrl = computed(() => `${requestURL.origin}/Veeso_og.jpg`);

useHead(() => ({
  title: currentConfig.value.meta.title,
  htmlAttrs: { lang: siteLocale.value },
  meta: [
    {
      name: 'description',
      content: currentConfig.value.meta.description,
    },
    {
      property: 'og:title',
      content: currentConfig.value.meta.title,
    },
    {
      property: 'og:description',
      content: currentConfig.value.meta.description,
    },
    {
      property: 'og:type',
      content: 'website',
    },
    {
      property: 'og:url',
      content: canonicalUrl.value,
    },
    { property: 'og:locale', content: ogLocaleFor(siteLocale.value) },
    {
      property: 'og:image',
      content: ogImageUrl.value,
    },
    {
      property: 'og:image:width',
      content: '1200',
    },
    {
      property: 'og:image:height',
      content: '630',
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image',
    },
    {
      name: 'twitter:title',
      content: currentConfig.value.meta.title,
    },
    {
      name: 'twitter:image',
      content: ogImageUrl.value,
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: canonicalUrl.value,
    },
    ...buildPathHreflangLinks(solutionRoutePath.value),
  ],
}));
</script>

<template>
  <div class="min-h-screen relative flex flex-col transition-colors duration-300">
    <AppNav :active-path="`/solutions/${slug}`" />

    <main class="relative z-[1] pt-[72px]">
      <!-- Hero Section -->
      <section class="py-24 px-6 md:px-12 max-w-[1320px] mx-auto" aria-label="hero-section">
        <div class="w-full md:w-[50%] text-left">
          <h1 class="text-3xl md:text-5xl font-bold text-black/90 tracking-tight mb-2">
            {{ currentConfig.heroTitle }}
          </h1>
          <p class="text-xl md:text-2xl leading-relaxed text-black/60 font-normal mb-8">
            {{ currentConfig.heroSubtitle }}
          </p>
          <NuxtLink
            :to="solutionPath(currentConfig.ctaLink)"
            class="inline-block bg-black/90 text-white/90 text-lg font-medium px-12 py-3 rounded-full transition-all duration-200 hover:bg-black"
          >
            {{ currentConfig.ctaText }}
          </NuxtLink>
        </div>
      </section>

      <!-- Features Section -->
      <section
        v-if="currentConfig.features && currentConfig.features.length > 0"
        class="max-w-[1320px] mx-auto px-6 md:px-12 pb-24"
        aria-label="features-section"
      >
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div
            v-for="(feature, index) in currentConfig.features"
            :key="index"
            class="flex flex-col"
          >
            <h3 class="text-xl md:text-2xl font-normal text-black/90 mb-4">
              {{ feature.title }}
            </h3>
            <p class="text-base text-black/60 font-normal leading-relaxed">
              {{ feature.description }}
            </p>
          </div>
        </div>
      </section>

      <!-- Team Sections -->
      <section class="bg-white" aria-label="team-sections">
        <div class="max-w-[1320px] mx-auto px-6 md:px-12 py-24">
          <div class="mb-12 text-center">
            <h2 class="text-[clamp(1.5rem,4vw,2.5rem)] font-normal text-black/90 mb-4">
              {{ t('landingPages.solutions.detail.team_cta') }}
            </h2>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div
              v-for="team in teamCards"
              :key="team.slug"
              class="flex flex-col p-6 rounded-2xl bg-black/[0.03] hover:bg-black/5 transition-all duration-300"
            >
              <h3 class="text-lg font-normal text-black/90 mb-2">
                {{ team.title }}
              </h3>
              <p class="text-sm text-black/60">
                {{ team.description }}
              </p>
              <NuxtLink
                :to="solutionPath(`/solutions/${team.slug}`)"
                class="mt-4 text-sm font-medium text-black/80 hover:text-black transition-colors"
              >
                {{ t('landingPages.solutions.index.card.learn_more') }} →
              </NuxtLink>
            </div>
          </div>
        </div>
      </section>

      <!-- FAQ Section -->
      <section class="py-24 bg-[#fafafa]">
        <div class="container mx-auto px-6 md:px-4">
          <div class="max-w-4xl mx-auto">
            <h2 class="text-4xl font-medium text-black/90 mb-12 text-center">
              {{ faqSectionTitle }}
            </h2>
            <div v-if="faqItems.length > 0" class="space-y-4">
              <div
                v-for="(item, index) in faqItems"
                :key="index"
                class="bg-black/[0.03] rounded-3xl px-8 py-6 transition-all duration-300"
              >
                <button
                  type="button"
                  @click="toggleFaq(index)"
                  class="flex items-center justify-between w-full text-left focus:outline-none"
                >
                  <h3 class="text-xl font-medium text-black/90 pr-4">{{ item.question }}</h3>
                  <span
                    class="transform transition-transform duration-300 text-2xl shrink-0"
                    :class="{ 'rotate-45': expandedFaqIndex === index }"
                  >
                    +
                  </span>
                </button>
                <div
                  class="grid transition-all duration-300 ease-in-out overflow-hidden"
                  :class="
                    expandedFaqIndex === index
                      ? 'grid-rows-[1fr] opacity-100 mt-4'
                      : 'grid-rows-[0fr] opacity-0'
                  "
                >
                  <div class="overflow-hidden">
                    <p class="text-black/60 leading-relaxed text-lg">{{ item.answer }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- CTA Section -->
      <FeaturesRoles />
    </main>
    <AppFooter />
  </div>
</template>

<style scoped>
/* 可以添加自定义样式 */
</style>
